import { useState } from "react";
import { toast } from "sonner";
import { sendApplication } from "@/lib/api";
import { SALON } from "@/data/content";

const STELLEN = [
  { title: "Friseur/in (Voll- oder Teilzeit)", text: "Du bist Geselle, Gesellin oder Meister/in und liebst Beratung genauso wie Handwerk? Dann komm zu uns – von Balayage bis Herrenschnitt ist hier alles gefragt.", tag: "Festanstellung" },
  { title: "Auszubildende/r Friseur/in", text: "Du willst das Handwerk von Grund auf lernen – mit Geduld, echten Kunden und einem Team, das sich Zeit nimmt? Dann starte deine Ausbildung bei uns.", tag: "Ausbildung" },
];

const VORTEILE = [
  "Familiäres, wertschätzendes Team",
  "Geregelte Zeiten – sonntags & montags frei",
  "Unterstützung bei Weiterbildungen",
  "Ruhiges, entspanntes Arbeitsklima",
  "Eigene Kundenberatung von Anfang an",
];

const SCHRITTE = [
  { nr: "01", title: "Formular absenden", text: "Name, Telefonnummer, Position – fertig. Ein Anschreiben brauchst du nicht." },
  { nr: "02", title: "Wir rufen zurück", text: "Wir melden uns schnell bei dir und vereinbaren ein unkompliziertes Kennenlernen." },
  { nr: "03", title: "Kennenlernen im Salon", text: "Schau dir alles in Ruhe an, lerne das Team kennen – und dann besprechen wir den Rest." },
];

export default function Karriere() {
  const [form, setForm] = useState({ name: "", phone: "", position: "Friseur/in (Voll- oder Teilzeit)", message: "", consent: false });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimme der Datenverarbeitung zu.");
      return;
    }
    setSending(true);
    try {
      await sendApplication({ name: form.name, phone: form.phone, position: form.position, message: form.message });
      toast.success("Danke für deine Bewerbung! Wir melden uns schnell bei dir.");
      setForm({ name: "", phone: "", position: "Friseur/in (Voll- oder Teilzeit)", message: "", consent: false });
    } catch (err) {
      toast.error(`Das hat nicht geklappt. Ruf uns gerne direkt an: ${SALON.phoneDisplay}.`);
    } finally {
      setSending(false);
    }
  };

  const whatsappPlaceholder = () => toast.info("Unser WhatsApp-Kanal kommt bald – nutze bis dahin gern das Formular oder ruf einfach an.");

  return (
    <div data-testid="page-karriere">
      <section className="py-16 md:py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Karriere im Salon</span>
          <h1 className="text-[clamp(36px,4.5vw,64px)] leading-tight mb-6">Werde Teil vom Haarstudio Nes</h1>
          <p className="text-base text-[#24211D]/80 max-w-2xl leading-relaxed">
            Wir wachsen und suchen dich: Friseur/in in Voll- oder Teilzeit und Auszubildende. Kurzbewerbung ohne Anschreiben ist herzlich willkommen – wir rufen einfach zurück.
          </p>
        </div>
      </section>

      <section className="py-16 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl mb-10">Offene Stellen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {STELLEN.map((s) => (
              <div key={s.title} className="border border-[#24211D]/15 bg-[#FFFFFF] p-8" data-testid={`stelle-${s.title.toLowerCase().includes("ausbildung") ? "ausbildung" : "friseur"}`}>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">{s.tag}</span>
                <h3 className="text-2xl mb-3">{s.title}</h3>
                <p className="text-xs text-[#24211D]/75 leading-relaxed font-sans">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#24211D] text-[#FBF8F3] py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Arbeiten bei Nes</span>
            <h2 className="text-3xl md:text-4xl mb-6">Deine Vorteile</h2>
            <ul className="space-y-3">
              {VORTEILE.map((v) => (
                <li key={v} className="text-sm text-[#FBF8F3]/85 border-b border-[#FBF8F3]/10 pb-3 flex gap-3">
                  <span className="text-[#B08D57]">—</span>{v}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#FBF8F3]/50 font-sans mt-4">Details besprechen wir persönlich – sag uns einfach, was dir wichtig ist.</p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">So einfach geht's</span>
            <h2 className="text-3xl md:text-4xl mb-6">Bewerbung in 3 Schritten</h2>
            <div className="space-y-6">
              {SCHRITTE.map((s) => (
                <div key={s.nr} className="border-t border-[#B08D57]/40 pt-4" data-testid={`bewerbung-schritt-${s.nr}`}>
                  <span className="text-xs font-display text-[#B08D57] block mb-1">{s.nr}</span>
                  <h3 className="text-lg mb-1">{s.title}</h3>
                  <p className="text-xs text-[#FBF8F3]/70 leading-relaxed font-sans">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Kurzbewerbung</span>
            <h2 className="text-3xl md:text-5xl mb-3">Unkompliziert bewerben</h2>
            <p className="text-xs text-[#24211D]/75 font-sans">Ohne Anschreiben, ohne Foto – Name und Telefonnummer reichen. Deinen Lebenslauf kannst du einfach per E-Mail an nezahatozan62@gmail.com nachreichen.</p>
          </div>
          <form onSubmit={submit} className="bg-[#FFFFFF] border border-[#24211D]/15 p-8 sm:p-10 space-y-6" data-testid="career-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="career-name" className="nes-label">Name *</label>
                <input id="career-name" data-testid="career-name-input" type="text" required className="nes-input" placeholder="Dein Name" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="career-phone" className="nes-label">Telefonnummer *</label>
                <input id="career-phone" data-testid="career-phone-input" type="tel" required className="nes-input" placeholder="02238 ..." value={form.phone} onChange={set("phone")} />
              </div>
            </div>
            <div>
              <label htmlFor="career-position" className="nes-label">Position *</label>
              <select id="career-position" data-testid="career-position-select" required className="nes-input" value={form.position} onChange={set("position")}>
                {STELLEN.map((s) => (
                  <option key={s.title} value={s.title}>{s.title}</option>
                ))}
                <option value="Quereinstieg / Sonstiges">Quereinstieg / Sonstiges</option>
              </select>
            </div>
            <div>
              <label htmlFor="career-message" className="nes-label">Nachricht (optional)</label>
              <textarea id="career-message" data-testid="career-message-input" rows={3} className="nes-input resize-none" placeholder="Erzähl kurz von dir – Erfahrung, Wünsche, Starttermin ..." value={form.message} onChange={set("message")} />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="career-consent-checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#B08D57]" />
              <span className="text-[11px] text-[#24211D]/70 leading-relaxed font-sans">
                Ich stimme zu, dass meine Angaben zur Bearbeitung der Bewerbung verarbeitet werden. Details in der Datenschutzerklärung. *
              </span>
            </label>
            <div className="pt-2 flex flex-col sm:flex-row gap-4 justify-center">
              <button type="submit" disabled={sending} data-testid="career-form-submit-button" className="bg-[#B08D57] hover:bg-[#967442] disabled:opacity-60 text-[#FBF8F3] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300">
                {sending ? "Wird gesendet ..." : "Bewerbung absenden"}
              </button>
              <button type="button" onClick={whatsappPlaceholder} data-testid="career-whatsapp-btn" className="border border-[#24211D]/30 text-[#24211D]/70 px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans hover:border-[#24211D] transition-colors duration-300">
                Per WhatsApp bewerben (bald)
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
